import {
  CKEditor,
  ClassicEditor,
  Form,
  TextEditorControl,
} from '@foxtaibox/foxtailbox-frontend-core';
import { SendOutlined } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';
import useInsertTracking from 'hooks/useInsertTracking';
import useVoiceRecorded, { RecordStatus } from 'hooks/useVoiceRecorded';
import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import VoiceRecordButton from '../../Single/components/VoiceRecordButton';
import { InputFormInputs, OnInputSubmit } from '../types';

interface InputProps {
  onSubmit: OnInputSubmit;
  disabled: boolean;
}

const Input = ({ onSubmit, disabled }: InputProps) => {
  const { t } = useTranslation();
  const { handleEditorReady } = useInsertTracking();
  const form = useForm<InputFormInputs>({
    defaultValues: {
      message: '',
      voiceRecord: undefined,
    },
  });
  const { control, getValues, handleSubmit, setValue } = form;
  const inputRef = useRef<CKEditor<ClassicEditor>>(null);
  const voiceRecord = useWatch({ control, name: 'voiceRecord' });
  const voiceRecorded = useVoiceRecorded({
    onTranscriptionSuccess: (transcribedText) => {
      setValue('message', `${getValues('message')} ${transcribedText}`.trim(), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  });
  const isVoiceTranscriptionInProgress = Boolean(voiceRecord) && voiceRecorded.isTranscribing;
  const isAudioConverting = voiceRecorded.status === RecordStatus.Converting;

  const submitHandler = (formData: InputFormInputs) => {
    onSubmit(formData).then((res) => {
      if (res) {
        voiceRecorded.removeRecorder(() => undefined);
        form.reset();
      }
    });
  };

  useEffect(() => {
    if (!disabled) {
      // TODO: как-будто не работает сейчас, inputRef.current = null
      inputRef.current?.editor?.focus();
    }
  }, [disabled]);

  return (
    <Form form={form}>
      <Stack
        alignItems='flex-end'
        spacing={3}
        sx={{
          p: 1,
          border: '1px solid',
          borderColor: 'border',
          borderRadius: 4,
          '& .MuiStack-root': { width: '100%' },
        }}>
        <TextEditorControl
          inputRef={inputRef}
          disabled={disabled}
          name='message'
          onReady={handleEditorReady}
          placeholder={t('assessment.question.interview.inputPlaceholder')}
          boxProps={{
            sx: {
              '&.editor-container .ck.ck-editor__main>.ck-editor__editable': {
                minHeight: 140,
                boxShadow: 'none',
                border: 'none',
              },
              '&.editor-container .ck.ck-sticky-panel__content': {
                border: 'none',
              },
            },
          }}
        />
        <Stack direction='row' justifyContent='flex-end' spacing={1}>
          <VoiceRecordButton voiceRecorded={voiceRecorded} dense />
          <IconButton
            onClick={handleSubmit(submitHandler)}
            disabled={disabled || isAudioConverting || isVoiceTranscriptionInProgress}>
            <SendOutlined sx={{ color: 'primary.main' }} />
          </IconButton>
        </Stack>
      </Stack>
    </Form>
  );
};

export default Input;
